import { Media, Post, PostResponse, SiteInfo } from "@/models/Cms";
import { getNewContent, getPopularContent, getPostByCategory, getPostBySearch, getPostBySlug, getSiteContentByTitle } from "@/service/post";

// Cache interface for storing CMS data
interface CMSCache {
    newContent?: { [key: string]: Post[] };
    popularContent?: { [key: string]: Post[] };
    postBySlug?: { [key: string]: Post };
    siteInfoByTitle?: { [key: string]: SiteInfo };
    contentByCategory?: { [key: string]: PostResponse };
}

// Initialize cache on window object if not exists
declare global {
    interface Window {
        __CMS_CACHE__?: CMSCache;
    }
}

// Helper function to get or initialize cache
function getCache(): CMSCache {
    if (typeof window !== 'undefined' && !window.__CMS_CACHE__) {
        window.__CMS_CACHE__ = {};
    }
    return window.__CMS_CACHE__ || {};
}

// Helper function to create cache key
function createCacheKey(...args: (string | number | undefined)[]): string {
    return args.filter(arg => arg !== undefined).join('_');
}

// Helper function to check if live mode is enabled
function isLiveMode(): boolean {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('live') === 'true';
}


export async function loadNewContent(pageSize = 3, useCache = true): Promise<Post[]> {
    const cache = getCache();
    const cacheKey = createCacheKey('newContent', pageSize);
    const liveMode = isLiveMode();
    
    // Check cache first only if not in live mode and useCache is true
    if (useCache && !liveMode && cache.newContent?.[cacheKey]) {
        return cache.newContent[cacheKey];
    }
    
    // Fetch from API
    const data = await (await getNewContent(pageSize)).data.data;
    
    // Always store in cache if useCache is true (even in live mode)
    if (useCache) {
        if (!cache.newContent) cache.newContent = {};
        cache.newContent[cacheKey] = data;
    }
    
    return data;
}

export async function loadPopularContent(pageSize = 3, useCache = true): Promise<Post[]> {
    const cache = getCache();
    const cacheKey = createCacheKey('popularContent', pageSize);
    const liveMode = isLiveMode();
    
    // Check cache first only if not in live mode and useCache is true
    if (useCache && !liveMode && cache.popularContent?.[cacheKey]) {
        return cache.popularContent[cacheKey];
    }
    
    // Fetch from API
    const data = await (await getPopularContent(pageSize)).data.data;
    
    // Always store in cache if useCache is true (even in live mode)
    if (useCache) {
        if (!cache.popularContent) cache.popularContent = {};
        cache.popularContent[cacheKey] = data;
    }
    
    return data;
}

export async function queryPostBySlug(slug: string, useCache = true): Promise<Post | undefined> {
    const cache = getCache();
    const cacheKey = createCacheKey('postBySlug', slug);
    const liveMode = isLiveMode();
    
    // Check cache first only if not in live mode and useCache is true
    if (useCache && !liveMode && cache.postBySlug?.[cacheKey]) {
        return cache.postBySlug[cacheKey];
    }
    
    // Fetch from API
    const posts = await (await getPostBySlug(slug)).data.data;
    const result = posts.length > 0 ? posts[0] : undefined;
    
    // Always store in cache if useCache is true and result is not undefined (even in live mode)
    if (useCache && result !== undefined) {
        if (!cache.postBySlug) cache.postBySlug = {};
        cache.postBySlug[cacheKey] = result;
    }
    
    return result;
}

export async function getContentByCategory(category: string | undefined, page = 1, useCache = true): Promise<PostResponse> {
    const cache = getCache();
    const cacheKey = createCacheKey('contentByCategory', category, page);
    const liveMode = isLiveMode();
    
    // Check cache first only if not in live mode and useCache is true
    if (useCache && !liveMode && cache.contentByCategory?.[cacheKey]) {
        return cache.contentByCategory[cacheKey];
    }
    
    // Fetch from API
    const postResponse = await (await getPostByCategory(category, page)).data;
    
    // Always store in cache if useCache is true (even in live mode)
    if (useCache) {
        if (!cache.contentByCategory) cache.contentByCategory = {};
        cache.contentByCategory[cacheKey] = postResponse;
    }
    
    return postResponse;
}

export async function getContentBySearch(search: string, page = 1): Promise<PostResponse> {
    // Always fetch fresh data for search content (no caching)
    const postResponse = await (await getPostBySearch(search, page)).data;
    return postResponse;
}

export async function getSiteInfoByTitle(title: string, useCache = true): Promise<SiteInfo | undefined> {
    const cache = getCache();
    const cacheKey = createCacheKey('siteInfoByTitle', title);
    const liveMode = isLiveMode();
    
    // Check cache first only if not in live mode and useCache is true
    if (useCache && !liveMode && cache.siteInfoByTitle?.[cacheKey]) {
        return cache.siteInfoByTitle[cacheKey];
    }
    
    // Fetch from API
    const siteInfoResponse = await (await getSiteContentByTitle(title)).data;
    const result = siteInfoResponse.data.length > 0 ? siteInfoResponse.data[0] : undefined;
    
    // Always store in cache if useCache is true and result is not undefined (even in live mode)
    if (useCache && result !== undefined) {
        if (!cache.siteInfoByTitle) cache.siteInfoByTitle = {};
        cache.siteInfoByTitle[cacheKey] = result;
    }
    
    return result;
}

export function getMediaUrls(media: Media): {
  original: string;
  large?: string;
  medium?: string;
  small?: string;
  thumbnail?: string;
} {
  if (!media || !media.attributes) return { original: '' };
  const { url, formats } = media.attributes;
  return {
    original: url,
    large: formats?.large?.url,
    medium: formats?.medium?.url,
    small: formats?.small?.url,
    thumbnail: formats?.thumbnail?.url,
  };
}

export function getMediaSrcSet(media: Media): string {
  const urls = getMediaUrls(media);
  const srcset = [];
  if (urls.large) srcset.push(`${urls.large} 1024w`);
  if (urls.medium) srcset.push(`${urls.medium} 768w`);
  if (urls.small) srcset.push(`${urls.small} 480w`);
  if (urls.thumbnail) srcset.push(`${urls.thumbnail} 200w`);
  if (urls.original) srcset.push(`${urls.original} 1920w`);
  return srcset.join(', ');
}

// Function to populate cache with data (used by prerender script)
export function populateCache(cacheData: CMSCache): void {
    if (typeof window !== 'undefined') {
        window.__CMS_CACHE__ = cacheData;
    }
}

// Function to get current cache data (used by prerender script)
export function getCacheData(): CMSCache {
    return getCache();
}